import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useProject } from './useProject';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

export interface RealtimeSubmission {
  id: number;
  jotform_submission_id: string;
  jotform_form_id: string;
  email: string | null;
  code: string | null;
  is_referral: boolean;
  report_url: string | null;
  jotform_created_at: string;
  created_at: string;
  raw_answers: Record<string, unknown> | null;
}

interface UseRealtimeSubmissionsOptions {
  limit?: number;
  enabled?: boolean;
}

export function useRealtimeSubmissions(options: UseRealtimeSubmissionsOptions = {}) {
  const { limit = 5, enabled = true } = options;
  const { currentProject } = useProject();
  const projectId = currentProject === '3m' ? 1 : 2;

  const [submissions, setSubmissions] = useState<RealtimeSubmission[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Cargar datos iniciales
  const loadInitialData = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('jotform_submissions')
        .select('id, jotform_submission_id, jotform_form_id, email, code, is_referral, report_url, jotform_created_at, created_at, raw_answers')
        .eq('project_id', projectId)
        .eq('is_deleted_in_jotform', false)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error loading initial submissions:', error);
        return;
      }

      if (data) {
        setSubmissions(data as RealtimeSubmission[]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId, limit, enabled]);

  useEffect(() => {
    if (!enabled) {
      setSubmissions([]);
      setIsConnected(false);
      return;
    }

    loadInitialData();

    // Crear canal único para este proyecto
    const channelName = `dashboard-submissions-${projectId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jotform_submissions',
          filter: `project_id=eq.${projectId}`
        },
        (payload: RealtimePostgresChangesPayload<RealtimeSubmission & { is_deleted_in_jotform?: boolean }>) => {
          if (payload.eventType === 'INSERT') {
            const newSubmission = payload.new as RealtimeSubmission & { is_deleted_in_jotform?: boolean };
            // Ignorar submissions marcadas como eliminadas
            if (newSubmission.is_deleted_in_jotform) {
              return;
            }
            setSubmissions(prev => {
              // Evitar duplicados
              if (prev.some(s => s.id === newSubmission.id)) {
                return prev;
              }
              return [newSubmission, ...prev].slice(0, limit);
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedSubmission = payload.new as RealtimeSubmission & { is_deleted_in_jotform?: boolean };
            // Si se marcó como eliminada, removerla de la lista
            if (updatedSubmission.is_deleted_in_jotform) {
              setSubmissions(prev => prev.filter(s => s.id !== updatedSubmission.id));
            } else {
              setSubmissions(prev =>
                prev.map(s => s.id === updatedSubmission.id ? updatedSubmission : s)
              );
            }
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as { id?: number })?.id;
            if (deletedId) {
              setSubmissions(prev => prev.filter(s => s.id !== deletedId));
            }
          }
        }
      )
      .subscribe((status) => {
        setIsConnected(status === 'SUBSCRIBED');
      });

    return () => {
      supabase.removeChannel(channel);
      setIsConnected(false);
    };
  }, [projectId, limit, enabled, loadInitialData]);

  // Función para refrescar manualmente
  const refresh = useCallback(() => {
    loadInitialData();
  }, [loadInitialData]);

  return {
    submissions,
    isConnected,
    isLoading,
    refresh
  };
}
